/**
 * The quote's two non-price states, shared by both purchase steps (the JSX
 * was a character-for-character copy in each). Booking's steps keep their own
 * copies for now — converging all four is a separate cleanup.
 *
 * Both are static markup — no state, no effects (NativeWebFirst).
 *
 * @module pages/subscription/steps/QuoteBanners
 */

import { RetryBanner } from '@butter/ui/components/feedback';

/**
 * The quote could not be fetched at all (network / 5xx). Distinct from a
 * refusal: there is nothing wrong with the plan, so offer a retry rather
 * than a reason.
 */
export function QuoteFailedBanner({ onRetry, testId }: { onRetry: () => void; testId: string }) {
  return (
    <RetryBanner
      message="We couldn't get a price for this plan."
      onRetry={onRetry}
      testId={testId}
    />
  );
}

/**
 * The `?promo=` capture's two states (7a). `applied` renders from the moment
 * the code is captured ("will be applied") or priced (the server label);
 * `refused` carries the quote's `promotion.message` — the member learns a
 * dead link before paying, and the purchase still works at list price.
 *
 * The BOX itself now lives beside `PromoCodeDisclosure` in
 * `@butter/ui/domains/subscriptions` (one home for the variant styling —
 * this file's copy had already started to drift from the disclosure's);
 * re-exported here so the steps' `'./QuoteBanners'` imports stay put.
 */
export { PromoNotice } from '@butter/ui/domains/subscriptions';

/**
 * The server refused to price the purchase (`isValid: false`). Takes priority
 * over the price card: there is no price to show, only the reason there
 * isn't one.
 */
export function QuoteRefusedPanel({ errors, testId }: { errors: string[]; testId: string }) {
  return (
    <div
      className="rounded-2xl border border-red-200 bg-red-50 p-4"
      role="alert"
      data-testid={testId}
    >
      {errors.map((err, i) => (
        <p key={i} className="text-sm text-red-800">{err}</p>
      ))}
    </div>
  );
}
